import { useEffect } from 'react';
import { ObservableLevelsEnum } from '../data/enums';
import { AssetModel, InspectionModel } from '../data/types';
import {
  InspectionViewModel,
  MineLocationViewModel,
} from '../data/viewmodels.types';
import useMergedState from './useMergedState';
import DatabaseInspectionsMethods from '../data/database.inspections.methods';
import { inspectionDB } from '../data/database.structure';

export const useAssetMineLocationLevel = ({
  inspectionId,
  inspection,
}: {
  inspectionId?: string;
  inspection?: InspectionModel;
}) => {
  const [state, setState] = useMergedState<{
    asset?: AssetModel | undefined;
    mineLocation?: MineLocationViewModel | undefined;
    entityId: number;
    entityExternalId: string;
    observableLevelsEnum: ObservableLevelsEnum;
    siteId: number;
    customerId: number;
    siteName: string;
    customerName: string;
    isLoading: boolean;
    error?: string;
  }>({
    isLoading: true,
    entityId: -1,
    entityExternalId: '',
    observableLevelsEnum: ObservableLevelsEnum.asset,
    siteId: -1,
    customerId: -1,
    siteName: '',
    customerName: '',
  });

  const setDataState = (data: InspectionViewModel) => {
    setState({
      asset: data.asset,
      mineLocation: data.mineLocation,
      entityId: data.asset?.id ?? data.mineLocation?.mineLocation.id ?? -1,
      entityExternalId:
        data.asset?.externalId ??
        data.mineLocation?.mineLocation.externalId ??
        '',
      observableLevelsEnum: data.asset
        ? ObservableLevelsEnum.asset
        : data.mineLocation
          ? ObservableLevelsEnum.mineLocation
          : ObservableLevelsEnum.asset,
      siteId: data.asset?.siteId ?? data.mineLocation?.siteId ?? -1,
      customerId: data.asset?.customerId ?? data.mineLocation?.customerId ?? -1,
      siteName: data.asset?.siteName ?? data.mineLocation?.siteName ?? '',
      customerName:
        data.asset?.customerName ?? data.mineLocation?.customerName ?? '',
      isLoading: false,
      error: undefined,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (inspectionId) {
          const dbMethods = new DatabaseInspectionsMethods(inspectionDB);
          const inspection =
            await dbMethods.getInspectionByExternalId(inspectionId);

          if (inspection) {
            setDataState(inspection);
          } else {
            setState({ error: 'Inspection not found' });
          }
        } else if (inspection) {
          const dbMethods = new DatabaseInspectionsMethods(inspectionDB);
          const ins = await dbMethods.getInspectionByExternalId(
            inspection.externalId,
          );

          if (ins) {
            setDataState(ins);
          } else {
            setState({ error: 'Inspection not found' });
          }
        }
      } catch (error) {
        console.error(error);
        setState({ error: 'Error fetching data' });
      } finally {
        setState({ isLoading: false });
      }
    };

    if (!inspectionId && !inspection) {
      setState({ error: 'No inspectionId or inspection provided' });
      return;
    }

    if (inspectionId) {
      fetchData();
    }
  }, [inspectionId, inspection]);

  return { ...state };
};
